import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import './InternetApplyModal.css';

interface StoreInfo {
  storeName: string;
  storePhone: string;
  storeAddress: string;
}

interface ProductInfo {
  carrier: string;
  internetName: string | null;
  tvName: string | null;
  monthlyPrice: number;
  cashGift: number;
}

interface FormData {
  // 개인정보
  name: string;
  residentNumber: string;
  phone: string;
  currentCarrier: string;
  installAddress: string;
  installAddressDetail: string;
  installDate: string;
  email: string;
  // 요금납부정보
  paymentMethod: 'card' | 'bank';
  cardCompany: string;
  cardHolder: string;
  cardNumber: string;
  cardExpiry: string;
  // 계좌이체 정보
  paymentBankName: string;
  paymentAccountHolder: string;
  paymentAccountNumber: string;
  // 입금정보
  accountHolder: string;
  bankName: string;
  accountNumber: string;
  // 동의
  agreePrivacy: boolean;
}

interface InternetApplyModalProps {
  roomId: string;
  onClose: () => void;
}

const InternetApplyModal: React.FC<InternetApplyModalProps> = ({ roomId, onClose }) => {
  const { customer } = useAuth();

  const [storeInfo, setStoreInfo] = useState<StoreInfo>({
    storeName: '',
    storePhone: '',
    storeAddress: '',
  });

  const [productInfo, setProductInfo] = useState<ProductInfo>({
    carrier: '',
    internetName: null,
    tvName: null,
    monthlyPrice: 0,
    cashGift: 0,
  });

  const [formData, setFormData] = useState<FormData>({
    name: '',
    residentNumber: '',
    phone: '',
    currentCarrier: '',
    installAddress: '',
    installAddressDetail: '',
    installDate: '',
    email: '',
    paymentMethod: 'card',
    cardCompany: '',
    cardHolder: '',
    cardNumber: '',
    cardExpiry: '',
    paymentBankName: '',
    paymentAccountHolder: '',
    paymentAccountNumber: '',
    accountHolder: '',
    bankName: '',
    accountNumber: '',
    agreePrivacy: false,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // 회사 정보 가져오기
  useEffect(() => {
    const fetchStoreInfo = async () => {
      try {
        const { data, error } = await supabase
          .from('app_settings')
          .select('setting_key, setting_value')
          .in('setting_key', ['company_name', 'customer_center', 'address']);

        if (error) throw error;

        const settings: Record<string, string> = {};
        data?.forEach(item => {
          settings[item.setting_key] = item.setting_value;
        });

        setStoreInfo({
          storeName: settings['company_name'] || '',
          storePhone: settings['customer_center'] || '',
          storeAddress: settings['address'] || '',
        });
      } catch (error) {
        console.error('매장 정보 조회 오류:', error);
      }
    };

    fetchStoreInfo();
  }, []);

  // 채팅방 상품 정보 가져오기
  useEffect(() => {
    const fetchProductInfo = async () => {
      try {
        const { data, error } = await supabase
          .from('chat_rooms')
          .select('carrier, internet_name, tv_name, monthly_price, cash_gift')
          .eq('id', roomId)
          .single();

        if (error) throw error;

        if (data) {
          // 통신사 코드를 한글로 변환
          const carrierMap: Record<string, string> = {
            'sk': 'SK브로드밴드',
            'kt': 'KT',
            'lg': 'LG유플러스',
          };

          setProductInfo({
            carrier: carrierMap[data.carrier] || data.carrier || '',
            internetName: data.internet_name,
            tvName: data.tv_name,
            monthlyPrice: data.monthly_price || 0,
            cashGift: data.cash_gift || 0,
          });
        }
      } catch (error) {
        console.error('상품 정보 조회 오류:', error);
      }
    };

    if (roomId) {
      fetchProductInfo();
    }
  }, [roomId]);

  // 고객 정보 자동 입력
  useEffect(() => {
    if (customer) {
      setFormData(prev => ({
        ...prev,
        name: customer.name || '',
        phone: customer.phone || '',
        currentCarrier: customer.carrier || '',
      }));
    }
  }, [customer]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // 주민번호 자동 포맷 (6자리 후 - 자동 삽입)
  const handleResidentNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/[^0-9]/g, ''); // 숫자만 허용

    if (value.length > 6) {
      value = value.slice(0, 6) + '-' + value.slice(6, 13);
    }

    setFormData(prev => ({ ...prev, residentNumber: value }));
  };

  // 다음 주소 검색 API
  const handleAddressSearch = () => {
    new (window as any).daum.Postcode({
      oncomplete: function(data: any) {
        const roadAddr = data.roadAddress;
        const jibunAddr = data.jibunAddress;

        setFormData(prev => ({
          ...prev,
          installAddress: roadAddr || jibunAddr
        }));
      }
    }).open();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.phone || !formData.installAddress) {
      alert('필수 항목을 모두 입력해주세요.');
      return;
    }

    if (!formData.agreePrivacy) {
      alert('개인정보 제3자 제공에 동의해주세요.');
      return;
    }

    setIsSubmitting(true);

    try {
      const { error } = await supabase
        .from('internet_applications')
        .insert([{
          customer_id: customer?.id,
          room_id: roomId,
          name: formData.name,
          resident_number: formData.residentNumber || null,
          phone: formData.phone,
          current_carrier: formData.currentCarrier || null,
          install_address: formData.installAddress,
          install_address_detail: formData.installAddressDetail || null,
          install_date: formData.installDate || null,
          email: formData.email || null,
          payment_method: formData.paymentMethod,
          card_company: formData.cardCompany || null,
          card_holder: formData.cardHolder || null,
          card_number: formData.cardNumber || null,
          card_expiry: formData.cardExpiry || null,
          payment_bank_name: formData.paymentBankName || null,
          payment_account_holder: formData.paymentAccountHolder || null,
          payment_account_number: formData.paymentAccountNumber || null,
          account_holder: formData.accountHolder || null,
          bank_name: formData.bankName || null,
          account_number: formData.accountNumber || null,
          status: 'pending',
        }]);

      if (error) throw error;

      if (roomId) {
        // 신청서 카드 메시지 전송
        await supabase.from('messages').insert([{
          room_id: roomId,
          sender_id: '00000000-0000-0000-0000-000000000000',
          message_type: 'application_card',
          content: JSON.stringify({
            type: 'internet_application',
            title: '인터넷 가입 신청서',
            applicant_name: formData.name,
            application_id: null // 나중에 조회용
          }),
          is_read: false
        }]);
      }

      setIsSubmitted(true);
    } catch (error) {
      console.error('신청서 제출 오류:', error);
      alert('신청서 제출에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // 배경 클릭으로 닫히지 않도록 함
    e.stopPropagation();
  };

  const today = new Date().toISOString().split('T')[0];

  if (isSubmitted) {
    return (
      <div className="apply-modal-overlay" onClick={handleBackdropClick}>
        <div className="apply-modal-container">
          <div className="apply-success">
            <div className="success-icon">
              <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
                <circle cx="32" cy="32" r="32" fill="#eee0af"/>
                <path d="M20 32L28 40L44 24" stroke="#1e1e1e" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h2>신청이 완료되었습니다</h2>
            <p>담당 상담원이 빠른 시일 내에<br/>연락드리겠습니다.</p>
            <button className="close-btn" onClick={onClose}>
              닫기
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="apply-modal-overlay" onClick={handleBackdropClick}>
      <div className="apply-modal-container">
        <div className="apply-modal-header">
          <h1>인터넷/TV 가입신청서</h1>
          <button className="modal-close-btn" onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6L18 18" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>

        <form className="apply-modal-form" onSubmit={handleSubmit}>
          {/* 가입(약속) 내역 */}
          <div className="section-title">가입(약속) 내역</div>

          <div className="info-group">
            <div className="info-row">
              <span className="info-label">매장명</span>
              <span className="info-value">{storeInfo.storeName || '-'}</span>
            </div>
            <div className="info-row">
              <span className="info-label">전화번호</span>
              <span className="info-value">{storeInfo.storePhone || '-'}</span>
            </div>
            <div className="info-row">
              <span className="info-label">주소</span>
              <span className="info-value">{storeInfo.storeAddress || '-'}</span>
            </div>
          </div>

          <div className="form-divider" />

          <div className="info-group">
            <div className="info-row">
              <span className="info-label">신청일</span>
              <span className="info-value">{today}</span>
            </div>
            <div className="info-row">
              <span className="info-label">통신사</span>
              <span className="info-value">{productInfo.carrier || '-'}</span>
            </div>
            <div className="info-row">
              <span className="info-label">상품구성</span>
              <span className="info-value">
                {productInfo.internetName && productInfo.tvName
                  ? '인터넷 + TV'
                  : productInfo.internetName
                    ? '인터넷'
                    : productInfo.tvName
                      ? 'TV'
                      : '-'}
              </span>
            </div>
            <div className="info-row">
              <span className="info-label">요금제</span>
              <span className="info-value">
                {[productInfo.internetName, productInfo.tvName].filter(Boolean).join(' + ') || '-'}
              </span>
            </div>
            <div className="info-row">
              <span className="info-label">월요금</span>
              <span className="info-value">
                {productInfo.monthlyPrice
                  ? `${productInfo.monthlyPrice.toLocaleString()}원`
                  : '-'}
              </span>
            </div>
            <div className="info-row">
              <span className="info-label">약속지원금</span>
              <span className="info-value">
                {productInfo.cashGift > 0
                  ? `${productInfo.cashGift.toLocaleString()}원`
                  : '-'}
              </span>
            </div>
          </div>

          <div className="form-divider" />

          {/* 고객 가입 정보 */}
          <div className="section-title">고객 가입 정보</div>
          <p className="section-subtitle">고객님 직접 입력</p>

          <div className="subsection-title">개인정보</div>

          <div className="form-group">
            <label htmlFor="name">이름(본명) <span className="required">*</span></label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="이름을 입력해주세요."
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="residentNumber">주민번호</label>
            <input
              type="text"
              id="residentNumber"
              name="residentNumber"
              value={formData.residentNumber}
              onChange={handleResidentNumberChange}
              placeholder="숫자만 입력해주세요."
              maxLength={14}
            />
          </div>

          <div className="form-group">
            <label htmlFor="phone">휴대폰번호 <span className="required">*</span></label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="숫자만 입력해주세요."
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="currentCarrier">현재 이용중인 통신사</label>
            <select
              id="currentCarrier"
              name="currentCarrier"
              value={formData.currentCarrier}
              onChange={handleChange}
            >
              <option value="">선택해주세요.</option>
              <option value="SKT">SKT</option>
              <option value="KT">KT</option>
              <option value="LG">LG U+</option>
              <option value="알뜰폰">알뜰폰</option>
              <option value="없음">없음 (신규)</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="installAddress">설치주소 <span className="required">*</span></label>
            <input
              type="text"
              id="installAddress"
              name="installAddress"
              value={formData.installAddress}
              onClick={handleAddressSearch}
              readOnly
              placeholder="클릭하여 주소 검색"
              required
              style={{ cursor: 'pointer' }}
            />
          </div>

          <div className="form-group">
            <label htmlFor="installAddressDetail">상세주소</label>
            <input
              type="text"
              id="installAddressDetail"
              name="installAddressDetail"
              value={formData.installAddressDetail}
              onChange={handleChange}
              placeholder="상세주소를 입력해주세요."
            />
          </div>

          <div className="form-group">
            <label htmlFor="installDate">희망설치일</label>
            <input
              type="date"
              id="installDate"
              name="installDate"
              value={formData.installDate}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">이메일</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="이메일을 입력해주세요."
            />
          </div>

          <div className="form-divider" />

          {/* 요금납부정보 */}
          <div className="subsection-title">요금납부정보(자동이체)</div>
          <p className="info-notice">인터넷 명의자와 납부자가 일치해야 합니다. 설치 후에는 고객센터를 통해 납부자를 변경하실 수 있습니다.</p>

          <div className="form-group toggle-group">
            <div className="toggle-buttons full-width">
              <button
                type="button"
                className={`toggle-btn ${formData.paymentMethod === 'card' ? 'active' : ''}`}
                onClick={() => setFormData(prev => ({ ...prev, paymentMethod: 'card' }))}
              >
                카드결제
              </button>
              <button
                type="button"
                className={`toggle-btn ${formData.paymentMethod === 'bank' ? 'active' : ''}`}
                onClick={() => setFormData(prev => ({ ...prev, paymentMethod: 'bank' }))}
              >
                계좌이체
              </button>
            </div>
          </div>

          {formData.paymentMethod === 'card' && (
            <>
              <div className="form-group">
                <label htmlFor="cardCompany">카드사</label>
                <select
                  id="cardCompany"
                  name="cardCompany"
                  value={formData.cardCompany}
                  onChange={handleChange}
                >
                  <option value="">선택해주세요.</option>
                  <option value="신한">신한</option>
                  <option value="삼성">삼성</option>
                  <option value="현대">현대</option>
                  <option value="KB국민">KB국민</option>
                  <option value="롯데">롯데</option>
                  <option value="하나">하나</option>
                  <option value="우리">우리</option>
                  <option value="NH농협">NH농협</option>
                  <option value="BC">BC</option>
                  <option value="기타">기타</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="cardHolder">카드주(카드명의)</label>
                <input
                  type="text"
                  id="cardHolder"
                  name="cardHolder"
                  value={formData.cardHolder}
                  onChange={handleChange}
                  placeholder="카드 명의자를 입력해주세요."
                />
              </div>

              <div className="form-group">
                <label htmlFor="cardNumber">카드번호</label>
                <input
                  type="text"
                  id="cardNumber"
                  name="cardNumber"
                  value={formData.cardNumber}
                  onChange={handleChange}
                  placeholder="카드번호를 입력해주세요."
                  maxLength={19}
                />
              </div>

              <div className="form-group">
                <label htmlFor="cardExpiry">카드 유효기간</label>
                <input
                  type="text"
                  id="cardExpiry"
                  name="cardExpiry"
                  value={formData.cardExpiry}
                  onChange={handleChange}
                  placeholder="MM/YY"
                  maxLength={5}
                />
              </div>
            </>
          )}

          {formData.paymentMethod === 'bank' && (
            <>
              <div className="form-group">
                <label htmlFor="paymentBankName">은행명</label>
                <select
                  id="paymentBankName"
                  name="paymentBankName"
                  value={formData.paymentBankName}
                  onChange={handleChange}
                >
                  <option value="">선택해주세요.</option>
                  <option value="KB국민">KB국민</option>
                  <option value="신한">신한</option>
                  <option value="우리">우리</option>
                  <option value="하나">하나</option>
                  <option value="NH농협">NH농협</option>
                  <option value="IBK기업">IBK기업</option>
                  <option value="카카오뱅크">카카오뱅크</option>
                  <option value="토스뱅크">토스뱅크</option>
                  <option value="케이뱅크">케이뱅크</option>
                  <option value="우체국">우체국</option>
                  <option value="새마을금고">새마을금고</option>
                  <option value="기타">기타</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="paymentAccountHolder">예금주명</label>
                <input
                  type="text"
                  id="paymentAccountHolder"
                  name="paymentAccountHolder"
                  value={formData.paymentAccountHolder}
                  onChange={handleChange}
                  placeholder="예금주명을 입력해주세요."
                />
              </div>

              <div className="form-group">
                <label htmlFor="paymentAccountNumber">계좌번호</label>
                <input
                  type="text"
                  id="paymentAccountNumber"
                  name="paymentAccountNumber"
                  value={formData.paymentAccountNumber}
                  onChange={handleChange}
                  placeholder="계좌번호를 입력해주세요."
                />
              </div>
            </>
          )}

          <div className="form-divider" />

          {/* 입금정보 */}
          <div className="subsection-title">입금정보 (지원금수령용)</div>

          <div className="form-group">
            <label htmlFor="bankName">은행명</label>
            <select
              id="bankName"
              name="bankName"
              value={formData.bankName}
              onChange={handleChange}
            >
              <option value="">선택해주세요.</option>
              <option value="KB국민">KB국민</option>
              <option value="신한">신한</option>
              <option value="우리">우리</option>
              <option value="하나">하나</option>
              <option value="NH농협">NH농협</option>
              <option value="IBK기업">IBK기업</option>
              <option value="카카오뱅크">카카오뱅크</option>
              <option value="토스뱅크">토스뱅크</option>
              <option value="케이뱅크">케이뱅크</option>
              <option value="우체국">우체국</option>
              <option value="새마을금고">새마을금고</option>
              <option value="기타">기타</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="accountHolder">예금주명</label>
            <input
              type="text"
              id="accountHolder"
              name="accountHolder"
              value={formData.accountHolder}
              onChange={handleChange}
              placeholder="예금주명을 입력해주세요."
            />
          </div>

          <div className="form-group">
            <label htmlFor="accountNumber">계좌번호</label>
            <input
              type="text"
              id="accountNumber"
              name="accountNumber"
              value={formData.accountNumber}
              onChange={handleChange}
              placeholder="계좌번호를 입력해주세요."
            />
          </div>

          <div className="form-divider" />

          {/* 필수 안내 사항 */}
          <div className="section-title">필수 안내 사항</div>
          <ul className="notice-list">
            <li>인터넷 3년약정 기준</li>
            <li>인터넷 설치후 1년이내 (사용일수) 해지시 사은품 100% 반환</li>
            <li>인터넷 개통 후 기존 사용하시던 인터넷회선은 고객이 직접 해지해야 함</li>
            <li>기존 인터넷 해지를 자동으로 원하실 경우에는 반드시 판매자에게 해지여부 요청을 해야 함</li>
            <li>인터넷 설치시 설치비 별도</li>
          </ul>

          <div className="form-divider" />

          {/* 동의 */}
          <div className="agreement-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={formData.agreePrivacy}
                onChange={(e) => setFormData(prev => ({ ...prev, agreePrivacy: e.target.checked }))}
              />
              <span className="checkbox-custom"></span>
              <span className="checkbox-text">개인정보 제3자 제공에 동의합니다.</span>
            </label>
            <p className="agreement-notice">기입하신 정보는 안전하게 보호되며, 해당 매장 외에는 공개되지 않습니다.</p>
          </div>

          <button type="submit" className="submit-btn" disabled={isSubmitting}>
            {isSubmitting ? '전송 중...' : '전송하기'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default InternetApplyModal;
